/**
 * Shared-list slice — prototype `quickAdd` (1695), `removeFromList` (1701),
 * `vote` (1796), `voteSet` (1764), `setIntAt` (1382), `nudgeInt` (1809),
 * `postC` (1873).
 *
 * The three vote writers have genuinely different semantics and all three are
 * ported (INVENTORY §8.3):
 *   `vote`     — toggle; does NOT touch `intOrder`; does NOT toast.
 *   `voteSet`  — set exactly; no-op when unchanged; moves the id to the END of
 *                `intOrder`; toasts.
 *   `setIntAt` — drag-drop; sets the vote AND splices into a position.
 */

import type { StateCreator } from 'zustand';

import {
  intOrderAfterDrop,
  intOrderAfterNudge,
  intOrderAfterVoteSet,
  intRows,
  LVL,
  rowKeyOf,
} from '../../lib/logic.ts';
import type { Comment, GameId, ListEntry, VoteInput, VoteValue } from '../../lib/types.ts';
import { mutate, storeApi } from '../mutate.ts';
import type { QdStore } from '../types.ts';

export interface ListSlice {
  /**
   * CALL IT (AA phase 5 §H; DESIGN-CHANGELOG 2026-09-03 "Adding a game asks
   * for your call"): the game a just-landed add is asking you to call; `0` =
   * closed. A call is OFFERED, never forced — SKIP closes it and the game
   * stays on the list uncalled.
   */
  askId: GameId;
  /**
   * ADD IT (docs/ADD-IT.md): the game Discover's `+` is asking WHERE to put —
   * the party list or your backlog; `0` = closed. A choice is offered, never
   * forced. Choosing closes this first and then runs the existing add, so the
   * party path's CALL IT (`askId`) never stacks on top of the chooser.
   */
  addId: GameId;
  openAdd: (id: GameId) => void;
  closeAdd: () => void;
  /** The chooser's two rows. Both close the chooser before they add. */
  addToTonight: () => Promise<void>;
  addToBacklog: () => Promise<void>;
  closeAsk: () => void;
  /** CALL IT's strip: the call, then the surface closes. */
  askCall: (l: VoteValue) => Promise<void>;

  list: ListEntry[];

  entryOf: (id: GameId) => ListEntry | null;
  myVote: (id: GameId) => VoteInput;

  quickAdd: (id: GameId) => Promise<void>;
  removeFromList: (id: GameId) => Promise<void>;
  vote: (id: GameId, l: VoteValue) => Promise<void>;
  voteSet: (id: GameId, l: VoteInput) => Promise<void>;
  setIntAt: (id: GameId, v: VoteInput, idx: number) => Promise<void>;
  nudgeInt: (id: GameId, dir: 1 | -1) => Promise<void>;
  postC: (id: GameId, body: string) => Promise<void>;
}

/**
 * `LETHAL COMPANY → A · WANT TO PLAY`, or `→ NO CALL` when the call is cleared.
 *
 * Read off `LVL` rather than spelled per letter: `voteSet` and `setIntAt` both
 * announce the same write, so four calls hand-written twice is eight strings to
 * keep in step with the buttons that produced them — and the toast is the only
 * confirmation a drag gets.
 */
const voteToast = (name: string, v: VoteInput): string =>
  name + ' → ' + (v ? LVL[v].g + ' · ' + LVL[v].t : 'NO CALL');

/**
 * Calling a game you had deferred retires the deferral — `intSkips` minus this
 * id, or `null` when there is nothing to retire and no write to make.
 *
 * The GOTY board does the same on the way in (`placeTier` filters `skips`, and
 * `place_tier` nulls `skip_pos` server-side). Without it the skip outlives the
 * call: clear the call months later and the game returns at the BACK of the
 * queue rather than in age order, and the list grows for the crew's lifetime
 * until "oldest first" is really "ever-skipped last".
 *
 * Only when the id is actually in the queue — a call is the hot path and must
 * not pay for a board write it has nothing to say.
 */
const clearedIntSkip = (skips: readonly GameId[], id: GameId, l: VoteInput): GameId[] | null =>
  l && skips.includes(id) ? skips.filter((x) => x !== id) : null;

const applyVote = (list: ListEntry[], id: GameId, k: string, v: VoteInput): ListEntry[] =>
  list.map((e) => {
    if (e.id !== id) return e;
    const votes = { ...e.votes };
    if (v) votes[k] = v;
    else delete votes[k];
    return { ...e, votes };
  });

export const createListSlice: StateCreator<QdStore, [], [], ListSlice> = (set, get) => ({
  askId: 0,
  addId: 0,
  list: [],

  entryOf: (id) => get().list.find((e) => e.id === id) ?? null,
  myVote: (id) => {
    const s = get();
    const e = s.list.find((x) => x.id === id);
    return rowKeyOf(e ? e.votes[s.session] : undefined);
  },

  quickAdd: async (id) => {
    const s = get();
    const m = s.me();
    if (!m) return;
    if (s.list.some((e) => e.id === id)) {
      s.toast('ALREADY ON THE LIST');
      return;
    }
    const optimisticEntry: ListEntry = {
      id,
      entryId: 'pending_' + id,
      by: m.k,
      at: Date.now(),
      votes: {},
      comments: [],
    };
    const created = await mutate(storeApi(get, set), {
      // The GAME id on purpose, and `patchEchoKey` agrees: the optimistic row
      // below has no server entry id yet, so an entry-keyed string could not
      // match its own echo. Same reasoning as `videoAddKey`'s (appid, key).
      key: 'add:' + id,
      optimistic: (st) => ({ list: [...st.list, optimisticEntry] }),
      commit: () => s.repo.quickAdd(id),
      toast: 'ADDED TO GAMES TONIGHT?',
    });
    // Swap the optimistic placeholder for the server row so later mutations
    // address the real handle.
    if (created) {
      set((st) => ({ list: st.list.map((e) => (e.id === id ? { ...created } : e)) }));
      // The add landed: ask for the call (CALL IT). After the swap, so the
      // vote goes to the server row and not the optimistic placeholder.
      set({ askId: id });
    }
  },

  openAdd: (id) => set({ addId: id }),
  closeAdd: () => set({ addId: 0 }),
  addToTonight: async () => {
    const id = get().addId;
    if (!id) return;
    set({ addId: 0 });
    await get().quickAdd(id);
  },
  addToBacklog: async () => {
    const id = get().addId;
    if (!id) return;
    set({ addId: 0 });
    await get().soloAdd(id);
  },

  closeAsk: () => set({ askId: 0 }),
  askCall: async (l) => {
    const id = get().askId;
    if (!id) return;
    set({ askId: 0 });
    await get().vote(id, l);
  },

  removeFromList: async (id) => {
    const s = get();
    const e = s.entryOf(id);
    if (!e) return;
    await mutate(storeApi(get, set), {
      // The ENTRY id — `entry.remove` carries nothing else, so `patchEchoKey`
      // has nothing else to key on. This read `'rmlist:' + id` (the GAME id)
      // until 2026-08-15: the two strings could never be equal, so §2.3 never
      // recognised the removal echo and a real race went untoasted. Unlike
      // `quickAdd`, the row being removed always has its server id already.
      key: 'rmlist:' + e.entryId,
      optimistic: (st) => ({ list: st.list.filter((x) => x.id !== id) }),
      commit: () => s.repo.removeFromList(e.entryId),
      toast: 'REMOVED FROM THE LIST',
    });
  },

  /** Toggle — the Detail `YOUR CALL` pair and the Interests list rows. */
  vote: async (id, l) => {
    const s = get();
    const m = s.me();
    const e = s.entryOf(id);
    if (!m || !e) return;
    const next: VoteInput = e.votes[m.k] === l ? '' : l;
    await mutate(storeApi(get, set), {
      key: 'vote:' + e.entryId + ':' + m.k,
      optimistic: (st) => ({ list: applyVote(st.list, id, m.k, next) }),
      commit: () => s.repo.setVote(e.entryId, next === '' ? null : next),
    });
  },

  /** Set exactly, and move the id to the end of my interests order. */
  voteSet: async (id, l) => {
    const s = get();
    const m = s.me();
    const e = s.entryOf(id);
    if (!m || !e) return;
    if ((e.votes[m.k] ?? '') === (l || '')) return; // no-op when unchanged
    const listIds = s.list.map((x) => x.id);
    const order = intOrderAfterVoteSet(s.intOrder[m.k] ?? [], listIds, id);
    const g = s.gById(id);
    const skips = clearedIntSkip(s.myIntSkips(), id, l);
    await mutate(storeApi(get, set), {
      key: 'vote:' + e.entryId + ':' + m.k,
      optimistic: (st) => ({
        list: applyVote(st.list, id, m.k, l),
        intOrder: { ...st.intOrder, [m.k]: order },
        ...(skips ? { intSkips: { ...st.intSkips, [m.k]: skips } } : {}),
      }),
      commit: async () => {
        await s.repo.setVote(e.entryId, l === '' ? null : l);
        await s.repo.setIntAt(order);
        if (skips) await s.repo.saveBoards({ intSkips: skips });
      },
      toast: voteToast(g ? g.n.toUpperCase() : 'GAME', l),
    });
  },

  /** Drag-drop: set the vote AND splice into position `idx` of the target row. */
  setIntAt: async (id, v, idx) => {
    const s = get();
    const m = s.me();
    const e = s.entryOf(id);
    if (!m || !e) return;
    const prev = rowKeyOf(e.votes[m.k]);
    // Rows are computed from the CURRENT votes, exactly as the prototype did
    // (its setState had not landed yet when it called intRowIds).
    const rows = intRows(s.list, m.k, s.intOrder[m.k] ?? []);
    const order = intOrderAfterDrop(rows, id, v, idx);
    const g = s.gById(id);
    const changed = prev !== v;
    const skips = clearedIntSkip(s.myIntSkips(), id, v);
    await mutate(storeApi(get, set), {
      key: 'vote:' + e.entryId + ':' + m.k,
      optimistic: (st) => ({
        list: applyVote(st.list, id, m.k, v),
        intOrder: { ...st.intOrder, [m.k]: order },
        ...(skips ? { intSkips: { ...st.intSkips, [m.k]: skips } } : {}),
      }),
      commit: async () => {
        if (changed) await s.repo.setVote(e.entryId, v === '' ? null : v);
        await s.repo.setIntAt(order);
        if (skips) await s.repo.saveBoards({ intSkips: skips });
      },
      ...(changed ? { toast: voteToast(g ? g.n.toUpperCase() : 'GAME', v) } : {}),
    });
  },

  nudgeInt: async (id, dir) => {
    const s = get();
    const m = s.me();
    if (!m) return;
    const v = s.myVote(id);
    const rows = intRows(s.list, m.k, s.intOrder[m.k] ?? []);
    const order = intOrderAfterNudge(rows, id, v, dir);
    if (!order) {
      s.toast(dir < 0 ? 'ALREADY FIRST' : 'ALREADY LAST');
      return;
    }
    await mutate(storeApi(get, set), {
      key: 'intOrder:' + m.k,
      optimistic: (st) => ({ intOrder: { ...st.intOrder, [m.k]: order } }),
      commit: () => s.repo.setIntAt(order),
      toast: dir < 0 ? 'MOVED EARLIER' : 'MOVED LATER',
    });
  },

  postC: async (id, body) => {
    const s = get();
    const m = s.me();
    const e = s.entryOf(id);
    const t = body.trim();
    if (!m || !e || !t) return;
    const optimistic: Comment = { id: 'pending_' + Date.now(), by: m.k, at: Date.now(), t };
    const created = await mutate(storeApi(get, set), {
      key: 'comment:' + e.entryId,
      optimistic: (st) => ({
        draft: '',
        list: st.list.map((x) => (x.id === id ? { ...x, comments: [...x.comments, optimistic] } : x)),
      }),
      commit: () => s.repo.postC(e.entryId, t),
      toast: 'COMMENT POSTED',
    });
    if (created) {
      set((st) => ({
        list: st.list.map((x) =>
          x.id === id
            ? { ...x, comments: x.comments.map((c) => (c.id === optimistic.id ? created : c)) }
            : x,
        ),
      }));
    }
  },
});
